<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12" sm="12" lg="5">
        <v-text-field
          v-model="search"
          prepend-icon="mdi-magnify"
          :label="$t('common.filters.filter')"
          variant="underlined"
          hide-details
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="6" lg="3">
        <v-select
          :items="timeRangeItems"
          item-value="value"
          v-model="selectedTimeRange"
          :label="$t('common.filters.timeRange')"
          :disabled="isLoading"
          variant="underlined"
          :menu-props="{ scrollStrategy: 'none' }"
        ></v-select>
      </v-col>
      <v-col cols="11" sm="5" lg="3">
        <v-menu
          ref="datePickerMenu"
          v-model="datePickerMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          scroll-strategy="none"
          max-width="290px"
          min-width="290px"
        >
          <template v-slot:activator="{ props }">
            <v-text-field
              v-model="dateRangeText"
              readonly
              :placeholder="$t('common.placeholders.selectDates')"
              :disabled="selectedTimeRange !== 'custom'"
              variant="underlined"
              clearable
              @click:clear="onClearDates"
              v-bind="props"
            ></v-text-field>
          </template>
          <v-date-picker v-model="dates" multiple="range">
            <template v-slot:actions>
              <v-spacer></v-spacer>
              <v-btn
                variant="text"
                color="primary"
                @click="datePickerMenu = false"
                >{{ $t("common.actions.cancel") }}</v-btn
              >
              <v-btn
                variant="text"
                color="primary"
                :disabled="dates.length < 2"
                @click="onSavePickedDate()"
                >{{ $t("common.actions.ok") }}</v-btn
              >
            </template>
          </v-date-picker>
        </v-menu>
      </v-col>
      <v-col cols="1" class="pl-0 d-flex justify-start align-center">
        <v-tooltip location="top">
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              icon
              variant="text"
              @click="fetchData"
              :loading="refreshLoading"
              :disabled="refreshLoading"
            >
              <v-icon>mdi-cached</v-icon>
              <template v-slot:loader>
                <span class="refreshLoader">
                  <v-icon>mdi-cached</v-icon>
                </span>
              </template>
            </v-btn>
          </template>
          <span>{{ $t("common.actions.refresh") }}</span>
        </v-tooltip>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-data-table
          :loading="isLoading"
          :headers="eventHeaders"
          :items="eventHistory"
          :items-per-page="15"
          :search="search"
          item-value="id"
          :sort-by="[{ key: 'receiveTime', order: 'desc' }]"
        >
          <template v-slot:[`item.source`]="{ item }">
            <span>{{ item.source }}</span>
          </template>
          <template v-slot:[`item.description`]="{ item }">
            <span>{{ item.description }}</span>
          </template>
          <template v-slot:[`item.receiveTime`]="{ item }">
            <span>{{ $filters.localeDateFilter(item.receiveTime) }}</span>
          </template>
          <template v-slot:[`item.extraData`]="{ item }">
            <span v-if="item.extraData" @click="showExtraData(item)">
              <v-tooltip location="top">
                <template v-slot:activator="{ props }">
                  <v-icon size="small" v-bind="props">mdi-information</v-icon>
                </template>
                <div
                  v-for="(part, index) in splitExtraData(item.extraData)"
                  :key="index"
                >
                  {{ part.name }}{{ part.name ? ":" : "" }}{{ part.value }}
                </div>
              </v-tooltip>
            </span>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <location-extra-info-dialog
      v-model="extraInfoDialog"
      :items="extraInfoItems"
    />
  </v-container>
</template>

<script>
import LimitationUtils from "@/utils/limitations"
import LocationExtraInfoDialog from "@/components/LocationExtraInfoDialog"

let limitationUtils = null

export default {
  name: "LocationEvents",
  props: ["machine"],
  components: { LocationExtraInfoDialog },
  data() {
    return {
      refreshLoading: true,
      search: "",
      dates: [],

      datePickerMenu: false,

      selectedTimeRange: "today",
      dateRangeText: "",

      extraInfoItems: null,
      extraInfoDialog: false
    }
  },
  beforeCreate() {
    limitationUtils = LimitationUtils()
  },
  async created() {
    this.setDateRange()
    await this.fetchData()
  },
  computed: {
    timeRangeItems() {
      return [
        { title: this.$t("common.enums.day.today"), value: "today" },
        {
          title: `${this.$t("common.filters.last")} ${this.$t(
            "common.units.day",
            7
          )}`,
          value: "7 days"
        },
        {
          title: `${this.$t("common.filters.last")} ${this.$t(
            "common.units.day",
            30
          )}`,
          value: "30 days"
        },
        { title: this.$t("common.filters.customRange"), value: "custom" }
      ]
    },
    isLoading() {
      return this.$store.getters.loadingGeolocationEvents
    },
    eventHeaders() {
      return [
        {
          title: this.$t("machines.location.events.dataTable.headers.source"),
          value: "source"
        },
        {
          title: this.$t(
            "machines.location.events.dataTable.headers.description"
          ),
          value: "description"
        },
        {
          title: this.$t("machines.location.events.dataTable.headers.date"),
          value: "receiveTime"
        },
        {
          title: this.$t("machines.location.events.dataTable.headers.extraData"),
          value: "extraData"
        }
      ]
    },
    eventHistory() {
      return this.$store.getters.geolocationEvents
    }
  },
  methods: {
    formatIsoDate(date) {
      // Dates set from the presets are already YYYY-MM-DD strings. The date
      // picker gives Date objects.
      if (typeof date === "string") {
        return date.substring(0, 10)
      }
      const month = String(date.getMonth() + 1).padStart(2, "0")
      const day = String(date.getDate()).padStart(2, "0")
      return `${date.getFullYear()}-${month}-${day}`
    },
    onClearDates() {
      // Clearing the custom range goes back to the default range ("today").
      // The selectedTimeRange watcher sets the dates and reloads the events.
      this.datePickerMenu = false
      this.selectedTimeRange = "today"
    },
    onSavePickedDate() {
      this.datePickerMenu = false
      // A range selection holds every date between start and end (sorted),
      // keep only the first and the last one.
      this.dates = [
        this.formatIsoDate(this.dates[0]),
        this.formatIsoDate(this.dates[this.dates.length - 1])
      ]
      this.fetchData()
    },
    async fetchData() {
      this.refreshLoading = true
      try {
        await this.$store.dispatch("fetchGeolocationEvents", {
          tenantUid: this.$route.params.tenantUid,
          serialNo: this.$route.params.serialNo,
          start: this.formatIsoDate(this.dates[0]),
          end: this.formatIsoDate(this.dates[this.dates.length - 1])
        })
      } catch (error) {
        // Error already handled
      } finally {
        this.refreshLoading = false
      }
    },
    setDateRange() {
      let startDate = new Date()
      let offset = 0

      if (this.selectedTimeRange === "today") {
        offset = 0
      } else if (this.selectedTimeRange === "7 days") {
        offset = 6 // Becomes 7 since today will be included
      } else if (this.selectedTimeRange === "30 days") {
        offset = 29 // Becomes 30 since today will be included
      }

      startDate.setDate(startDate.getDate() - offset)
      this.dates = [
        startDate.toISOString().substring(0, 10),
        new Date().toISOString().substring(0, 10)
      ]
    },
    splitExtraData(extraInfo) {
      // Maximum number of extras to display
      const MAX_NUM_EXTRA = 5

      let lines = this.formatExtraInfo(extraInfo)

      if (lines.length > MAX_NUM_EXTRA) {
        lines = lines.slice(0, MAX_NUM_EXTRA)
        lines.push({
          category: "-",
          name: "",
          value: `...${this.$t("machines.alarms.moreExists")}`
        })
      }
      return lines
    },
    showExtraData(item) {
      this.extraInfoItems = this.formatExtraInfo(item.extraData)
      this.extraInfoDialog = true
    },

    formatExtraInfo(data) {
      const extraInfo = JSON.parse(data)
      let results = this.getProperties(extraInfo)
      return results.sort((a, b) => {
        if ((a.category || b.category) && a.category != b.category) {
          return a.category > b.category ? 1 : -1
        }
        return a.name > b.name ? 1 : -1
      })
    },

    getProperties(extraInfo, category) {
      let results = []

      for (var propertyName in extraInfo) {
        const property = extraInfo[propertyName]
        if (propertyName == "position" && property) {
          results = results.concat(
            this.getProperties(
              property,
              this.$t("machines.location.geofencingSettings.coordinates")
            )
          )
        } else if (propertyName == "restrictions" && property) {
          results = results.concat(
            this.getRestrictions(
              property,
              this.$t("machines.location.geofencingSettings.limit.limitation")
            )
          )
        } else {
          results.push({
            category: category || "-",
            name: propertyName,
            value: property
          })
        }
      }
      return results
    },
    getRestrictions(rawData, category) {
      let results = []
      if (!rawData) {
        return results
      }
      const bitValues = [1, 2, 4, 8, 16]
      let values = bitValues.filter(bv => bv & rawData)
      results = values.map(i => {
        return {
          category: category || "-",
          name: limitationUtils.getText(i),
          value: "-"
        }
      })
      return results
    }
  },
  watch: {
    selectedTimeRange(newRange) {
      if (newRange !== "custom") {
        this.setDateRange()
        this.fetchData()
      } else {
        // Display date picker. OK callback will trigger fetch
        this.datePickerMenu = true
      }
    },
    dates(newDates) {
      if (!newDates || newDates.length < 2) {
        this.dateRangeText = ""
        return
      }
      const start = this.formatIsoDate(newDates[0])
      const end = this.formatIsoDate(newDates[newDates.length - 1])
      this.dateRangeText = `${start} ~ ${end}`
    }
  }
}
</script>

<style type="scss">
.refreshLoader {
  animation: loader 1s infinite;
  display: flex;
}
</style>
